import { createAsyncThunk } from '@reduxjs/toolkit';

import type { GameCreateRequest, GameDto, GameUpdateRequest } from '../../api/types';
import { entitiesActions } from '../slices/entitiesSlice';
import type { RootState } from '../store';

import { gamesService } from './_deps';

export const fetchGames = createAsyncThunk<void, { name?: string; tags?: string[] } | undefined, { state: RootState }>(
  'games/fetchAll',
  async (params, thunkApi) => {
    const res = await gamesService.listGames(params);
    thunkApi.dispatch(entitiesActions.setGames(res.items));
  },
);

export const fetchGameById = createAsyncThunk<GameDto, { gameId: string }, { state: RootState }>(
  'games/fetchById',
  async ({ gameId }, thunkApi) => {
    const game = await gamesService.getGame({ gameId });
    thunkApi.dispatch(entitiesActions.upsertGame(game));
    return game;
  },
);

export const createGame = createAsyncThunk<GameDto, GameCreateRequest, { state: RootState }>(
  'games/create',
  async (payload, thunkApi) => {
    const game = await gamesService.createGame({ payload });
    thunkApi.dispatch(entitiesActions.upsertGame(game));
    return game;
  },
);

export const updateGame = createAsyncThunk<GameDto, { gameId: string; payload: GameUpdateRequest }, { state: RootState }>(
  'games/update',
  async ({ gameId, payload }, thunkApi) => {
    const game = await gamesService.updateGame({ gameId, payload });
    thunkApi.dispatch(entitiesActions.upsertGame(game));
    return game;
  },
);

export const deleteGame = createAsyncThunk<void, { gameId: string }, { state: RootState }>(
  'games/delete',
  async ({ gameId }, thunkApi) => {
    await gamesService.deleteGame({ gameId });
    thunkApi.dispatch(entitiesActions.removeGame(gameId));
  },
);

export const fetchGameTags = createAsyncThunk<void, void, { state: RootState }>(
  'games/fetchTags',
  async (_, thunkApi) => {
    const tags = await gamesService.listGameTags();
    thunkApi.dispatch(entitiesActions.setGameTags(tags));
  },
);

export const fetchFavoriteGames = createAsyncThunk<void, void, { state: RootState }>(
  'games/fetchFavorites',
  async (_, thunkApi) => {
    const res = await gamesService.getFavoriteGames();
    const ids = res.items.map((g) => g.id);
    thunkApi.dispatch(entitiesActions.setFavoriteGameIds(ids));
  },
);

export const toggleFavoriteGame = createAsyncThunk<void, { gameId: string }, { state: RootState }>(
  'games/toggleFavorite',
  async ({ gameId }, thunkApi) => {
    const state = thunkApi.getState();
    const isFav = state.entities.games.favoriteIds.includes(gameId);

    if (isFav) {
      thunkApi.dispatch(entitiesActions.removeFavoriteGameId(gameId));
      await gamesService.removeGameFavorite({ gameId });
    } else {
      thunkApi.dispatch(entitiesActions.addFavoriteGameId(gameId));
      await gamesService.addGameFavorite({ gameId });
    }
  },
);
